/**
 * Tweaks store — runtime-configurable theme choices.
 *
 * Persists to `localStorage["bouw.tweaks"]` per
 * [TECH_STACK §3](../../../TECH_STACK.md#3-theming-contract).
 *
 * - accent: brand colour, mapped to `--accent` and `--accent-2` at runtime.
 * - density: drives `[data-density]` on `<html>` → `--row-h` / `--pad`.
 * - timelineStyle: switches Dashboard / Tijdlijn between milestones and gantt.
 */

import { useEffect } from 'react';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export type Density = 'compact' | 'default' | 'comfy';
export type TimelineStyle = 'milestones' | 'gantt';

export interface AccentPreset {
  id: string;
  label: string;
  /** oklch lightness/chroma/hue triples — applied as a single shorthand. */
  accent: string;
  accent2: string;
  accentTint: string;
  accentTint2: string;
}

export const ACCENT_PRESETS: AccentPreset[] = [
  {
    id: 'terracotta',
    label: 'Terracotta',
    accent: 'oklch(58% 0.13 38)',
    accent2: 'oklch(50% 0.14 38)',
    accentTint: 'oklch(94% 0.04 38)',
    accentTint2: 'oklch(88% 0.06 38)',
  },
  {
    id: 'mos',
    label: 'Mos',
    accent: 'oklch(52% 0.10 150)',
    accent2: 'oklch(44% 0.11 150)',
    accentTint: 'oklch(94% 0.035 150)',
    accentTint2: 'oklch(88% 0.05 150)',
  },
  {
    id: 'inkt',
    label: 'Inkt-blauw',
    accent: 'oklch(50% 0.11 250)',
    accent2: 'oklch(42% 0.12 250)',
    accentTint: 'oklch(94% 0.035 250)',
    accentTint2: 'oklch(88% 0.05 250)',
  },
  {
    id: 'klei',
    label: 'Klei',
    accent: 'oklch(54% 0.14 28)',
    accent2: 'oklch(46% 0.15 28)',
    accentTint: 'oklch(94% 0.04 28)',
    accentTint2: 'oklch(88% 0.06 28)',
  },
];

export interface TweaksState {
  accentId: string;
  density: Density;
  timelineStyle: TimelineStyle;
  setAccent: (id: string) => void;
  setDensity: (d: Density) => void;
  setTimelineStyle: (s: TimelineStyle) => void;
}

export const useTweaks = create<TweaksState>()(
  persist(
    (set) => ({
      accentId: 'terracotta',
      density: 'default',
      timelineStyle: 'milestones',
      setAccent: (accentId) => set({ accentId }),
      setDensity: (density) => set({ density }),
      setTimelineStyle: (timelineStyle) => set({ timelineStyle }),
    }),
    { name: 'bouw.tweaks' },
  ),
);

function applyAccent(accentId: string) {
  const preset = ACCENT_PRESETS.find((p) => p.id === accentId) ?? ACCENT_PRESETS[0];
  const root = document.documentElement;
  root.style.setProperty('--accent', preset.accent);
  root.style.setProperty('--accent-2', preset.accent2);
  root.style.setProperty('--accent-tint', preset.accentTint);
  root.style.setProperty('--accent-tint-2', preset.accentTint2);
}

function applyDensity(density: Density) {
  document.documentElement.setAttribute('data-density', density);
}

/** Mount once near the root — synchronises store → DOM. */
export function useApplyTweaks(): void {
  const accentId = useTweaks((s) => s.accentId);
  const density = useTweaks((s) => s.density);

  useEffect(() => {
    applyAccent(accentId);
  }, [accentId]);

  useEffect(() => {
    applyDensity(density);
  }, [density]);
}
